import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  FileText, Download, Mail, Trash2, RefreshCw, Calendar,
  BarChart3, Users, ChevronDown, ChevronUp, Plus, Send,
  CheckCircle2, Clock, ArrowLeft, Settings
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminReportsDashboard({ getAuthHeaders }) {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [overview, setOverview] = useState([]);
  const [scheduleConfig, setScheduleConfig] = useState({ enabled: false, day_of_month: 1, auto_email: true });
  const [showSettings, setShowSettings] = useState(false);
  const [emailModal, setEmailModal] = useState(null);
  const [emailTo, setEmailTo] = useState('');
  const [sending, setSending] = useState(false);

  const headers = useCallback(() => ({
    ...getAuthHeaders(),
    'Content-Type': 'application/json',
  }), [getAuthHeaders]);

  useEffect(() => {
    // Fetch client list from client manager (dashboard-clients endpoint)
    fetch(`${API}/api/admin/dashboard-clients`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { clients: [] })
      .then(d => setClients(d.clients || []))
      .catch(() => {});

    // Fetch overview
    fetch(`${API}/api/admin/reports/overview/all`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { clients: [] })
      .then(d => setOverview(d.clients || []))
      .catch(() => {});

    // Fetch schedule config
    fetch(`${API}/api/admin/reports/schedule/config`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { config: {} })
      .then(d => setScheduleConfig(d.config || { enabled: false, day_of_month: 1, auto_email: true }))
      .catch(() => {});
  }, [getAuthHeaders]);

  const selectClient = async (slug) => {
    setSelectedClient(slug);
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/reports/${slug}`, { headers: getAuthHeaders() });
      const data = await res.json();
      setReports(data.reports || []);
    } catch { setReports([]); }
    setLoading(false);
  };

  const generateReport = async (period = 'this_month') => {
    if (!selectedClient) return;
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/admin/reports/${selectedClient}/generate`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ period }),
      });
      const data = await res.json();
      if (data.success) {
        setReports(prev => [data.report, ...prev]);
      }
    } catch (e) { console.error(e); }
    setGenerating(false);
  };

  const downloadReport = (reportId) => {
    const token = localStorage.getItem('admin_token');
    window.open(`${API}/api/admin/reports/download/${reportId}?token=${token}`, '_blank');
  };

  const deleteReport = async (reportId) => {
    if (!window.confirm('Delete this report?')) return;
    await fetch(`${API}/api/admin/reports/${reportId}/delete`, { method: 'DELETE', headers: getAuthHeaders() });
    setReports(prev => prev.filter(r => r.report_id !== reportId));
  };

  const emailReport = async (reportId) => {
    if (!emailTo) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/api/admin/reports/${reportId}/email`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ email: emailTo }),
      });
      const data = await res.json();
      if (data.success) {
        setReports(prev => prev.map(r => r.report_id === reportId ? { ...r, emailed: true, emailed_to: emailTo } : r));
        setEmailModal(null);
        setEmailTo('');
      } else {
        alert(data.error || 'Failed to send');
      }
    } catch (e) { alert(e.message); }
    setSending(false);
  };

  const saveSchedule = async () => {
    await fetch(`${API}/api/admin/reports/schedule/config`, {
      method: 'PUT', headers: headers(),
      body: JSON.stringify(scheduleConfig),
    });
  };

  // Detail view for a selected client
  if (selectedClient) {
    const clientObj = clients.find(c => c.slug === selectedClient);
    const clientName = clientObj?.business_name || selectedClient;
    return (
      <div className="space-y-4" data-testid="report-detail">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="sm" onClick={() => setSelectedClient(null)} data-testid="report-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h2 className="text-lg font-semibold text-gray-900">{clientName} — Reports</h2>
        </div>

        {/* Generate buttons */}
        <Card className="bg-gray-50 border-gray-200">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-3 items-center">
              <span className="text-sm text-slate-300">Generate Report:</span>
              <Button
                size="sm"
                onClick={() => generateReport('this_month')}
                disabled={generating}
                data-testid="gen-this-month"
              >
                <Plus className="w-3 h-3 mr-1" /> {generating ? 'Generating...' : 'This Month'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => generateReport('last_month')}
                disabled={generating}
                data-testid="gen-last-month"
              >
                <Calendar className="w-3 h-3 mr-1" /> Last Month
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => generateReport('last_30')}
                disabled={generating}
                data-testid="gen-last-30"
              >
                Last 30 Days
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Reports List */}
        {loading ? (
          <div className="text-center py-8 text-slate-400">Loading reports...</div>
        ) : reports.length === 0 ? (
          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="p-8 text-center">
              <FileText className="w-10 h-10 text-slate-500 mx-auto mb-3" />
              <p className="text-slate-400">No reports yet. Generate one above.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {reports.map(report => (
              <Card key={report.report_id} className="bg-gray-50 border-gray-200" data-testid={`report-${report.report_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <FileText className="w-4 h-4 text-blue-400" />
                        <span className="text-sm font-medium text-gray-900">{report.period_label}</span>
                        {report.emailed && (
                          <Badge className="bg-green-600/20 text-green-400 text-[10px]">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Emailed
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-4 text-xs text-slate-400">
                        <span>Posts: {report.stats?.total_posts || 0}</span>
                        <span>SEO: {report.stats?.seo_progress_pct || 0}%</span>
                        <span>Leads: {report.stats?.total_leads || 0}</span>
                        <span>{report.generated_at ? new Date(report.generated_at).toLocaleDateString() : ''}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => downloadReport(report.report_id)} data-testid={`download-${report.report_id}`}>
                        <Download className="w-3 h-3 mr-1" /> PDF
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setEmailModal(report.report_id); setEmailTo(''); }}>
                        <Mail className="w-3 h-3 mr-1" /> Email
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteReport(report.report_id)}>
                        <Trash2 className="w-3 h-3 text-red-400" />
                      </Button>
                    </div>
                  </div>

                  {/* Email modal inline */}
                  {emailModal === report.report_id && (
                    <div className="mt-3 flex gap-2 items-center border-t border-gray-200 pt-3">
                      <Input
                        placeholder="client@email.com"
                        value={emailTo}
                        onChange={e => setEmailTo(e.target.value)}
                        className="flex-1 h-8 text-sm"
                        data-testid="email-input"
                      />
                      <Button size="sm" onClick={() => emailReport(report.report_id)} disabled={sending || !emailTo} data-testid="send-email-btn">
                        <Send className="w-3 h-3 mr-1" /> {sending ? 'Sending...' : 'Send'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEmailModal(null)}>Cancel</Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Overview / Client picker view
  return (
    <div className="space-y-6" data-testid="reports-dashboard">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-blue-400" /> Client Reports
        </h2>
        <Button size="sm" variant="outline" onClick={() => setShowSettings(!showSettings)} data-testid="schedule-settings-btn">
          <Settings className="w-3 h-3 mr-1" /> Schedule
        </Button>
      </div>

      {/* Auto-schedule config */}
      {showSettings && (
        <Card className="bg-gray-50 border-blue-500/30" data-testid="schedule-config">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-900">Monthly Auto-Report Schedule</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-4">
              <label className="text-sm text-slate-300">Enabled:</label>
              <Button
                size="sm"
                variant={scheduleConfig.enabled ? 'default' : 'outline'}
                onClick={() => setScheduleConfig(p => ({ ...p, enabled: !p.enabled }))}
              >
                {scheduleConfig.enabled ? 'On' : 'Off'}
              </Button>
            </div>
            <div className="flex items-center gap-4">
              <label className="text-sm text-slate-300">Day of Month:</label>
              <Input
                type="number"
                min={1}
                max={28}
                value={scheduleConfig.day_of_month}
                onChange={e => setScheduleConfig(p => ({ ...p, day_of_month: parseInt(e.target.value) || 1 }))}
                className="w-20 h-8"
              />
            </div>
            <div className="flex items-center gap-4">
              <label className="text-sm text-slate-300">Auto-email to clients:</label>
              <Button
                size="sm"
                variant={scheduleConfig.auto_email ? 'default' : 'outline'}
                onClick={() => setScheduleConfig(p => ({ ...p, auto_email: !p.auto_email }))}
              >
                {scheduleConfig.auto_email ? 'Yes' : 'No'}
              </Button>
            </div>
            <Button size="sm" onClick={saveSchedule}>Save Schedule</Button>
          </CardContent>
        </Card>
      )}

      {/* Reports Overview */}
      {overview.length > 0 && (
        <Card className="bg-gray-50 border-gray-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-900">Recent Report Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {overview.map(o => (
                <div
                  key={o.client_slug}
                  className="flex items-center justify-between p-2 bg-gray-100/50 rounded-lg cursor-pointer hover:bg-gray-100"
                  onClick={() => selectClient(o.client_slug)}
                >
                  <div>
                    <span className="text-sm text-gray-900 font-medium">{o.client_name || o.client_slug}</span>
                    <span className="text-xs text-slate-400 ml-3">{o.total_reports} reports</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    {o.latest_stats && (
                      <>
                        <Badge variant="outline" className="text-[10px]">Posts: {o.latest_stats.total_posts}</Badge>
                        <Badge variant="outline" className="text-[10px]">SEO: {o.latest_stats.seo_progress_pct}%</Badge>
                      </>
                    )}
                    <span>{o.latest_report ? new Date(o.latest_report).toLocaleDateString() : ''}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Clients */}
      <Card className="bg-gray-50 border-gray-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-gray-900 flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-400" /> Select Client to Manage Reports
          </CardTitle>
        </CardHeader>
        <CardContent>
          {clients.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">No clients found. Add clients via Client Manager.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {clients.map(c => (
                <Button
                  key={c.slug}
                  variant="outline"
                  className="justify-start h-auto py-2 px-3"
                  onClick={() => selectClient(c.slug)}
                  data-testid={`client-${c.slug}`}
                >
                  <div className="text-left">
                    <div className="text-sm font-medium">{c.business_name || c.name || c.slug}</div>
                    <div className="text-xs text-muted-foreground">{c.slug}</div>
                  </div>
                </Button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
